// Copyright (c) Ping. All rights reserved.
//
// Bridges the foundations `useTasks()` live-list world (flat
// `TaskRecord` rows from the DeviceGroup-only `TasksPersister`, §2.10)
// onto the legacy `<TasksScreen>` which speaks a richer branded
// `TaskItem` over a `load` / `subscribe` / `update` contract.
//
// The screen pre-dates the sync foundations; rather than fork its
// state machine we adapt the live `TaskRecord[]` into the screen's
// loader/subscriber surface and translate its mutation callbacks back
// into the persister + wire-envelope vocabulary. Hosts own the actual
// side effects (local-first persister write + a best-effort DeviceGroup
// `com.ping.task_upsert` / `task_delete` send) — this module is pure
// glue and ships no persistence of its own.

import { useCallback, useMemo, useRef } from 'react';

import type { TaskRecord } from '../sync';
import type { UserId } from '../util/ids';
import type {
  TaskId,
  TaskItem,
  TaskPriority,
  TaskState,
  TasksData,
  TasksLoader,
  TasksScreenProps,
  TasksSubscriber,
  TasksUpdater,
} from './legacy';

/**
 * Derive the lifecycle `TaskState` for a record. Prefer the explicit
 * persisted `state`; fall back to the legacy boolean `done`
 * (completed/pending) for older rows that pre-date the field.
 */
export function recordState(r: TaskRecord): TaskState {
  if (r.state !== undefined) return r.state;
  return r.done ? 'completed' : 'pending';
}

/** Resolve the persisted priority, defaulting to 'medium'. */
export function recordPriority(r: TaskRecord): TaskPriority {
  return r.priority ?? 'medium';
}

/**
 * Map a flat foundations `TaskRecord` onto the legacy screen's branded
 * `TaskItem`. The screen carries fields the wire model doesn't yet
 * persist (`priority`, `folderId`, `assigneeId`, lifecycle beyond a
 * boolean `done`); we project sensible defaults so the row renders and
 * round-trips losslessly for the fields that DO exist.
 */
export function taskRecordToItem(r: TaskRecord): TaskItem {
  return {
    id: r.id as TaskId,
    title: r.title,
    state: recordState(r),
    priority: recordPriority(r),
    dueAt: r.due ?? null,
    createdAt: r.updatedAt,
    updatedAt: r.updatedAt,
    folderId: null,
    assigneeId:
      r.assigneeId !== undefined && r.assigneeId !== ''
        ? (r.assigneeId as unknown as UserId)
        : null,
  };
}

/**
 * Project the richer fields off a `TaskItem` back onto a partial
 * `TaskRecord` (additive — only sets fields that carry meaning so the
 * spread stays undefined-safe). Pairs with `taskRecordToItem` for a
 * lossless round-trip of priority/state/assignee/dueAt.
 */
export function itemFieldsToRecord(item: {
  readonly state?: TaskState | undefined;
  readonly priority?: TaskPriority | undefined;
  readonly assigneeId?: UserId | null | undefined;
  readonly dueAt?: number | null | undefined;
  readonly description?: string | undefined;
}): Partial<TaskRecord> {
  const out: Partial<TaskRecord> = {};
  if (item.state !== undefined) {
    out.state = item.state;
    out.done = item.state === 'completed';
  }
  if (item.priority !== undefined) out.priority = item.priority;
  if (item.assigneeId !== undefined && item.assigneeId !== null) {
    out.assigneeId = item.assigneeId as unknown as string;
  }
  if (item.dueAt !== undefined && item.dueAt !== null) {
    out.due = item.dueAt;
  }
  if (item.description !== undefined) out.description = item.description;
  return out;
}

/** Project a live `TaskRecord[]` into the screen's loader bundle. */
export function recordsToData(rows: readonly TaskRecord[]): TasksData {
  return { folders: [], tasks: rows.map(taskRecordToItem) };
}

/** What a host must supply to satisfy a screen-driven mutation. */
export interface TaskRecordMutators {
  /**
   * Persist (last-writer-wins) the given record locally AND attempt the
   * DeviceGroup `com.ping.task_upsert` mirror. Called for completion
   * toggles and any inline edit the screen surfaces through `update`.
   */
  readonly upsert: (record: TaskRecord) => void | Promise<void>;
  /**
   * Remove the task locally AND attempt the DeviceGroup
   * `com.ping.task_delete` mirror.
   */
  readonly remove: (id: string) => void | Promise<void>;
}

/**
 * Build the `TasksScreen` props from a live `TaskRecord[]`.
 *
 * - `load` / `subscribe` replay the current rows (and re-emit whenever
 *   `rows` identity changes — `useTasks` returns a fresh array per
 *   change, so the screen re-renders the live set).
 * - `update` translates the screen's optimistic partial (`{ state }`)
 *   back onto the underlying record and routes it through
 *   `mutators.upsert`.
 * - `onBulkDelete` routes the selection toolbar's delete through
 *   `mutators.remove`.
 */
export function useTaskRecordScreenProps(
  rows: readonly TaskRecord[],
  mutators: TaskRecordMutators,
): Pick<TasksScreenProps, 'load' | 'subscribe' | 'update' | 'onBulkComplete' | 'onBulkDelete'> {
  const byId = useMemo(() => {
    const m = new Map<string, TaskRecord>();
    for (const r of rows) m.set(r.id, r);
    return m;
  }, [rows]);
  const byIdRef = useRef(byId);
  byIdRef.current = byId;

  const data = useMemo<TasksData>(() => recordsToData(rows), [rows]);
  const dataRef = useRef(data);
  dataRef.current = data;

  const load: TasksLoader = useCallback(
    async () => dataRef.current,
    [],
  );

  // The screen subscribes once per committed filter/query; emit the
  // current snapshot synchronously so the live list shows immediately,
  // and re-emit on every `rows` change via the effect below by handing
  // the callback through a ref the host re-invokes. Since `useTasks`
  // drives re-renders (fresh `rows` array), the screen's own
  // `load`-on-deps re-run already re-projects; `subscribe` just seeds.
  const subscribe: TasksSubscriber = useCallback((_filter, _query, cb) => {
    cb(dataRef.current);
    return () => {};
  }, []);

  const update: TasksUpdater = useCallback(
    async (id, partial) => {
      const current = byIdRef.current.get(id);
      if (!current) return;
      const nextDone =
        partial.state !== undefined ? partial.state === 'completed' : current.done;
      const nextDue =
        partial.dueAt !== undefined
          ? partial.dueAt ?? undefined
          : current.due;
      const nextTitle = partial.title !== undefined ? partial.title : current.title;
      const next: TaskRecord = {
        // Preserve all richer fields (priority/state/assignee/
        // description) the partial doesn't touch.
        ...current,
        id: current.id,
        title: nextTitle,
        position: current.position,
        updatedAt: Date.now(),
        ...(nextDue !== undefined ? { due: nextDue } : {}),
        ...(nextDone !== undefined ? { done: nextDone } : {}),
        ...(partial.state !== undefined ? { state: partial.state } : {}),
        ...(partial.priority !== undefined ? { priority: partial.priority } : {}),
        ...(current.list !== undefined ? { list: current.list } : {}),
      };
      await mutators.upsert(next);
    },
    [mutators],
  );

  const onBulkComplete = useCallback(
    (ids: ReadonlySet<TaskId>) => {
      for (const id of ids) {
        const current = byIdRef.current.get(id);
        if (!current || current.done) continue;
        const next: TaskRecord = {
          ...current,
          id: current.id,
          title: current.title,
          position: current.position,
          updatedAt: Date.now(),
          done: true,
          state: 'completed',
          ...(current.due !== undefined ? { due: current.due } : {}),
          ...(current.list !== undefined ? { list: current.list } : {}),
        };
        void mutators.upsert(next);
      }
    },
    [mutators],
  );

  const onBulkDelete = useCallback(
    (ids: ReadonlySet<TaskId>) => {
      for (const id of ids) void mutators.remove(id);
    },
    [mutators],
  );

  return { load, subscribe, update, onBulkComplete, onBulkDelete };
}
